import React, { Fragment, useEffect } from "react";

/**
 * ExamLayout wraps all exam experiences (mock, practice, etc.)
 * Screen capture / screenshot prevention is enforced here for exam integrity.
 * - Blocks PrintScreen, Ctrl+P, Ctrl+S, Ctrl+Shift+S, Win+Shift+S, PrtSc
 * - Disables right-click context menu
 * - Hides content during print (@media print)
 * - Disables text selection on exam content
 * - Blurs content when tab/window loses focus (screenshot tool detection)
 */
const ExamLayout = (props) => {
  useEffect(() => {
    // --- 1. Block screenshot / print keyboard shortcuts ---
    const handleKeyDown = (e) => {
      // PrintScreen
      if (e.key === "PrintScreen") {
        e.preventDefault();
        // Clear clipboard to prevent screenshot
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText("");
        }
        return false;
      }
      // Ctrl+P (print)
      if (e.ctrlKey && e.key === "p") {
        e.preventDefault();
        return false;
      }
      // Ctrl+S (save page)
      if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+S (Windows screenshot / save as)
      if (e.ctrlKey && e.shiftKey && e.key === "S") {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+I (DevTools)
      if (e.ctrlKey && e.shiftKey && e.key === "I") {
        e.preventDefault();
        return false;
      }
      // F12 (DevTools)
      if (e.key === "F12") {
        e.preventDefault();
        return false;
      }
      // Ctrl+U (View Source)
      if (e.ctrlKey && e.key === "u") {
        e.preventDefault();
        return false;
      }
    };

    // --- 2. Disable right-click context menu ---
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // --- 3. Blur content when window loses focus (hides from screenshot tools) ---
    const examContainer = document.querySelector("[data-exam-layout]");

    const handleVisibilityChange = () => {
      if (examContainer) {
        if (document.hidden) {
          examContainer.style.filter = "blur(20px)";
        } else {
          examContainer.style.filter = "none";
        }
      }
    };

    const handleWindowBlur = () => {
      if (examContainer) {
        examContainer.style.filter = "blur(20px)";
      }
    };

    const handleWindowFocus = () => {
      if (examContainer) {
        examContainer.style.filter = "none";
      }
    };

    // --- 4. Inject print-blocking CSS ---
    const style = document.createElement("style");
    style.id = "exam-screen-capture-block";
    style.textContent = `
      @media print {
        [data-exam-layout] {
          display: none !important;
          visibility: hidden !important;
        }
        body::after {
          content: "Screen capture is disabled during exams.";
          display: block;
          text-align: center;
          font-size: 24px;
          padding: 100px;
          color: #333;
        }
      }
      [data-exam-layout] {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }
    `;
    document.head.appendChild(style);

    // Attach listeners
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("keyup", (e) => {
      if (e.key === "PrintScreen") {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText("");
        }
      }
    }, true);
    document.addEventListener("contextmenu", handleContextMenu, true);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);

    // Cleanup on unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("contextmenu", handleContextMenu, true);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
      const injectedStyle = document.getElementById("exam-screen-capture-block");
      if (injectedStyle) injectedStyle.remove();
      if (examContainer) {
        examContainer.style.filter = "none";
      }
    };
  }, []);

  return (
    <Fragment>
      <div data-exam-layout="true">
        {props.children}
      </div>
    </Fragment>
  );
};

export default ExamLayout;
